import { Injectable, Injector, inject } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpInterceptor,
    HttpHeaders,
    HttpErrorResponse,
} from '@angular/common/http';
import { switchMap, catchError, throwError, BehaviorSubject, filter, take } from 'rxjs';
import { TokenService } from '../services/token.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private tokenService = inject(TokenService);
    private injector = inject(Injector);

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<
        string | null
    >(null);

    intercept(request: HttpRequest<unknown>, next: HttpHandler) {
        let headers: HttpHeaders = request.headers;
        if (request.headers.get('noauth')) {
            headers = headers.delete('noauth');
            return next.handle(request.clone({ headers }));
        }

        return this.tokenService.getToken().pipe(
            switchMap((token: string) => {
                request = this.addTokenHeader(request, token);
                return next.handle(request).pipe(
                    catchError((error) => {
                        if (error instanceof HttpErrorResponse && error.status === 401) {
                            return this.handle401Error(request, next);
                        }
                        return throwError(() => error);
                    }),
                );
            }),
        );
    }

    private handle401Error(request: HttpRequest<unknown>, next: HttpHandler) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.tokenService.getRefreshToken().pipe(
                switchMap((refreshToken: string) => {
                    return this.tokenService.refreshToken({ refresh: refreshToken }).pipe(
                        switchMap((response) => {
                            this.isRefreshing = false;
                            const newAccessToken = response.data!.access;
                            this.refreshTokenSubject.next(newAccessToken);
                            return next.handle(this.addTokenHeader(request, newAccessToken));
                        }),
                        catchError((err) => {
                            this.isRefreshing = false;
                            this.tokenService.logout().subscribe();
                            return throwError(() => err);
                        }),
                    );
                }),
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter((token) => token != null),
                take(1),
                switchMap((jwt) => {
                    return next.handle(this.addTokenHeader(request, jwt));
                }),
            );
        }
    }

    private addTokenHeader(request: HttpRequest<unknown>, token: string) {
        let headers = request.headers.append('Authorization', 'Bearer ' + token);
        const translate = this.injector.get(TranslateService);
        const currentLang = translate.currentLang || 'en';
        headers = headers.set('Accept-Language', currentLang);
        headers = headers.delete('noauth');
        return request.clone({ headers });
    }
}
